import { useCallback, useEffect, useState } from 'react';
import { errorMessage, financeGet, financeSend, ROLE_LABEL, type FinanceCtx, type FinanceRole } from '../financeApi';

type StaffRole = {
  id: number;
  userAccount: string;
  userNickName: string;
  role: 'super' | 'staff';
  financeRole: FinanceRole | 'none';
};

const LEVELS: { value: FinanceRole | 'none'; label: string; desc: string }[] = [
  { value: 'manager', label: '재정부장', desc: '모든 메뉴, 예산 확정, 결의서 재정부 승인, 재정 설정·권한 부여' },
  { value: 'clerk', label: '재정부원', desc: '수입·지출 등록/수정, 증빙 첨부, 결의서 작성·지급 처리, 기부금 영수증 발급' },
  { value: 'viewer', label: '조회 전용', desc: '담임목사·감사용. 모든 보고서·집행 현황 조회만 가능' },
  { value: 'none', label: '권한 없음', desc: '재정관리에 들어올 수 없음' },
];

// 재정 권한(재정부장 전용): 교회 담당자 계정별 재정 권한 등급. 최종관리자는 항상 재정부장.
// 교회앱관리 > 승인관리의 '재정 권한' 스위치와 같은 값을 쓴다(스위치로 켜면 재정부원).
export default function FinanceRoles({ ctx }: { ctx: FinanceCtx }) {
  const [rows, setRows] = useState<StaffRole[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<number | null>(null);

  const load = useCallback(() => {
    financeGet<StaffRole[]>(ctx, '/roles')
      .then((data) => setRows(data ?? []))
      .catch((err) => setError(errorMessage(err, '담당자 목록을 불러오지 못했습니다.')));
  }, [ctx]);

  useEffect(load, [load]);

  const change = (row: StaffRole, financeRole: StaffRole['financeRole']) => {
    setSavingId(row.id);
    setError(null);
    financeSend(ctx, 'put', `/roles/${row.id}`, { financeRole })
      .then(load)
      .catch((err) => setError(errorMessage(err, '권한을 바꾸지 못했습니다.')))
      .finally(() => setSavingId(null));
  };

  return (
    <div className="manage-panel">
      <div className="manage-panel-header">
        <div>
          <h2 className="manage-panel-title">재정 권한</h2>
          <p className="manage-panel-desc">교회 담당자 계정마다 재정관리 권한 등급을 정합니다. 교회 최종관리자는 항상 재정부장입니다.</p>
        </div>
      </div>

      <div className="fin-levels">
        {LEVELS.map((l) => (
          <div key={l.value} className="fin-level"><b>{l.label}</b><span>{l.desc}</span></div>
        ))}
      </div>

      {error && <div className="fin-inline-error">{error}</div>}

      <div className="manage-card-block">
        <div className="manage-table-wrap">
          <table className="manage-table">
            <thead>
              <tr><th>이름</th><th>계정</th><th style={{ width: 120 }}>교회 관리자</th><th style={{ width: 200 }}>재정 권한</th></tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={4} className="manage-table-empty">활성 담당자가 없습니다.</td></tr>}
              {rows.map((row) => (
                <tr key={row.id}>
                  <td><b>{row.userNickName || '-'}</b>{row.id === ctx.requesterId && <span className="fin-muted"> (나)</span>}</td>
                  <td className="manage-table-meta">{row.userAccount}</td>
                  <td>{row.role === 'super' ? '최종관리자' : '일반관리자'}</td>
                  <td>
                    {row.role === 'super' ? (
                      <span className="manage-badge manage-badge--purple">{ROLE_LABEL.manager} (고정)</span>
                    ) : (
                      <select className="fin-select" value={row.financeRole} disabled={savingId === row.id}
                        onChange={(e) => change(row, e.target.value as StaffRole['financeRole'])}>
                        {LEVELS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
                      </select>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
